import {
  CompressorData,
  PressureTypeRoomData,
  Asset,
  ModelSpec,
  Room as StoreRoom,
  Tank,
  PressureTypeRoom,
  Dryer,
  TanksType,
  DryerType,
  NO_MATCHING_SPEC,
} from '@/store/SiteSurveyForm/constants';
import { FormInstance } from 'antd';
import SiteSurveyStore from '@/store/SiteSurveyForm/SiteSurveyStore';

export interface RoomConfigurationContentProps {
  siteSurveyStore: SiteSurveyStore;
  isCustomerInformationStepOpen: boolean;
  isSiteConfigurationStepOpen: boolean;
  toggleCustomerInformationStep: () => void;
  toggleSiteConfigurationStep: () => void;
  renderCustomerInformationStepContent: () => React.ReactNode;
  form: FormInstance;
  handleValidationChange: (pressureType: string, isValid: boolean) => void;
  onCompressorDrawerVisibilityChange?: (isVisible: boolean) => void;
}

export interface AssetsAssignmentSectionProps {
  form: FormInstance;
  hasMultipleTargetPressures?: boolean;
  onValidationChange?: (isValid: boolean) => void;
  onChange?: (values: PressureTypeRoomData) => void;
  onDrawerVisibilityChange?: (isVisible: boolean) => void;
}

export interface Compressor extends Omit<CompressorData, 'number'> {
  id: string;
  number?: number;
  modelSpecTechnicalName?: string;
  isManual: boolean;
}

export interface Room {
  id: number;
  name: string;
  compressorData: Compressor[];
  compressorsInRoom: string[];
  tanks: ComponentTank[];
  newTank?: ComponentTank;
  dryers?: ComponentDryer[];
  newDryer?: ComponentDryer;
}

export interface ComponentTank {
  id: string;
  tankType: TanksType;
  tankValue: number;
}

export interface ComponentDryer {
  id: string;
  dryerType: DryerType;
}

export interface RoomConfigurationStepValues {
  assets?: Asset[];
  low?: PressureTypeRoomData;
  high?: PressureTypeRoomData;
  showThankYouScreen?: boolean;

  [key: string]: unknown;
}

export const TANK_OPTIONS = [
  { value: TanksType.DRY, label: 'Dry' },
  { value: TanksType.WET, label: 'Wet' },
];

export const DRYER_OPTIONS = [
  { value: DryerType.DESICCANT, label: 'Desiccant' },
  { value: DryerType.REFRIGERATED, label: 'Refrigerated' },
];

export interface CompressorOption {
  value: string;
  label: string;
}

export interface StoreRoomFormat {
  compressorsInRoom: string[];
  compressorData: CompressorData[];
  tanks: Array<{
    tankType: TanksType;
    tankValue: number;
  }>;
}

type ItemsMap = {
  tanks: ComponentTank;
  dryers: ComponentDryer;
};

export const convertToStoreFormat = (rooms: Room[]): StoreRoomFormat[] => {
  return rooms.map(room => {
    const compressorsInRoom = Array.isArray(room.compressorsInRoom) ? [...room.compressorsInRoom] : [];

    const compressorData = Array.isArray(room.compressorData)
      ? room.compressorData.map(compressor => {
          const compressorData: CompressorData = {
            number: compressor.number !== undefined ? compressor.number : 0,
            serialNumber: compressor.serialNumber,
            model: compressor.model,
            modelSpec: compressor.modelSpec,
            controller: compressor.controller,
            customSpecFlow: compressor.customSpecFlow,
            customSpecPower: compressor.customSpecPower,
            customSpecPressure: compressor.customSpecPressure,
            isManual: compressor.isManual,
            images: compressor.images
          };
          return compressorData;
        })
      : [];

    const tanks = Array.isArray(room.tanks)
      ? room.tanks.map(tank => {
          return {
            tankType: tank.tankType as TanksType,
            tankValue: tank.tankValue,
          };
        })
      : [];

    return {
      compressorsInRoom,
      compressorData,
      tanks,
    };
  });
};

export const isCompressorNumberDuplicate = (
  rooms: Room[],
  roomIndex: number,
  compressorIndex: number,
  number?: number,
): Record<number, Record<number, number>> => {
  if (number === undefined || number === null) return {};

  const duplicates = {};
  let amountOfNumber = 0;
  for (const [currentRoomIndex, room] of rooms.entries()) {
    if (room.compressorData) {
      for (const [indexComp, compressor] of room.compressorData.entries()) {
        if (compressor.number === number && (roomIndex !== currentRoomIndex || indexComp !== compressorIndex)) {
          if (!duplicates[currentRoomIndex]) {
            duplicates[currentRoomIndex] = {};
          }
          duplicates[currentRoomIndex][indexComp] = 1;
          amountOfNumber++;
        }
      }
    }
  }
  if (amountOfNumber > 0) {
    if (!duplicates[roomIndex]) {
      duplicates[roomIndex] = {};
    }
    duplicates[roomIndex][compressorIndex] = 1;
  }
  return duplicates || {};
};

export const getModelSpecOptions = (modelSpecs: ModelSpec[]): ModelSpec[] => {
  const options = modelSpecs
    .filter(spec => spec.technicalName)
    .map(spec => ({
      value: spec.id,
      label: spec.technicalName,
      modelDisplayName: spec.modelDisplayName,
      technicalName: spec.technicalName,
    }));

  return [
    {
      value: NO_MATCHING_SPEC,
      label: 'No matching spec',
      modelDisplayName: 'No matching spec',
      technicalName: 'No matching spec',
    },
    ...options,
  ] as unknown as ModelSpec[];
};

export const createRoomsFromStoreData = (storeRoom: StoreRoom | PressureTypeRoom, index: number): Room => {
  const timestamp = Date.now();
  const compressors = Array.isArray(storeRoom.compressorData)
    ? storeRoom.compressorData.map((compData: CompressorData, compIndex: number) => ({
        id: `room-${timestamp}-comp-${compIndex}`,
        serialNumber: compData.serialNumber,
        number: compData.number === 0 ? undefined : compData.number,
        model: compData.model,
        modelSpec: compData.modelSpec,
        controller: compData.controller,
        isManual: compData.isManual || false,
        images: compData.images,
        customSpecFlow: compData.customSpecFlow,
        customSpecPower: compData.customSpecPower,
        customSpecPressure: compData.customSpecPressure,
      }))
    : [];

  const tanks = Array.isArray(storeRoom.tanks)
    ? storeRoom.tanks.map((tank: Tank, tankIndex: number) => ({
        id: `room-${timestamp}-tank-${tankIndex}`,
        tankType: tank.tankType,
        tankValue: tank.tankValue,
      }))
    : [];

  const newTank: ComponentTank = {
    id: `room-${timestamp}-tank-new`,
    tankType: storeRoom.newTank?.tankType,
    tankValue: storeRoom.newTank?.tankValue,
  };

  const dryers = Array.isArray(storeRoom.dryers)
    ? storeRoom.dryers.map((dryer: Dryer, dryerIndex: number) => ({
        id: `room-${timestamp}-dryer-${dryerIndex}`,
        dryerType: dryer.dryerType,
      }))
    : [];

  const newDryer: ComponentDryer = {
    id: `room-${timestamp}-dryer-new`,
    dryerType: storeRoom.newDryer?.dryerType,
  };

  return {
    id: timestamp + index,
    name: `Room ${index + 1}`,
    compressorData: compressors,
    compressorsInRoom: Array.isArray(storeRoom.compressorsInRoom) ? storeRoom.compressorsInRoom : [],
    tanks: tanks,
    newTank,
    dryers: dryers,
    newDryer,
  };
};

export const getAvailableCompressorsForRoom = (rooms: Room[], roomId: number, assets: Asset[]): CompressorOption[] => {
  const selectedInOtherRooms = rooms.filter(room => room.id !== roomId).flatMap(room => room.compressorsInRoom);

  return assets
    .map(asset => ({
      value: asset.serialNumber,
      label: asset.serialNumber,
    }))
    .filter(compressor => !selectedInOtherRooms.includes(compressor.value));
};

export const hasDuplicateCompressorNumbers = (rooms: Room[]): boolean => {
  const usedNumbers = new Set<number>();
  let hasDuplicates = false;

  for (const room of rooms) {
    for (const compressor of room.compressorData) {
      if (compressor.number !== undefined && compressor.number !== null) {
        if (usedNumbers.has(compressor.number)) {
          hasDuplicates = true;
        }
        usedNumbers.add(compressor.number);
      }
    }
  }

  return hasDuplicates;
};

export const hasEmptyCompressorRooms = (rooms: Room[]): boolean => {
  const emptyRooms = rooms.filter(room => room.compressorData.length === 0);
  return emptyRooms.length > 0;
};

export const hasMissingCompressorNumbers = (rooms: Room[]): boolean => {
  let hasMissing = false;

  for (const room of rooms) {
    for (const compressor of room.compressorData) {
      if (compressor.number === null || compressor.number === undefined) {
        hasMissing = true;
      }
    }
  }

  return hasMissing;
};

export const removeItemFromRoom = <K extends keyof ItemsMap>(
  rooms: Room[],
  roomId: number,
  itemId: string,
  itemsKey: K,
): Room[] => {
  const roomIndex = rooms.findIndex(room => room.id === roomId);
  if (roomIndex === -1) return rooms;

  const updatedRooms = [...rooms];
  const items = updatedRooms[roomIndex][itemsKey];

  if (Array.isArray(items)) {
    updatedRooms[roomIndex][itemsKey] = items.filter((item): item is ItemsMap[K] => item.id !== itemId) as Room[K];
  }

  return updatedRooms;
};

export const removeTank = (rooms: Room[], roomId: number, tankId: string): Room[] => {
  return removeItemFromRoom(rooms, roomId, tankId, 'tanks');
};

export const removeDryer = (rooms: Room[], roomId: number, dryerId: string): Room[] => {
  return removeItemFromRoom(rooms, roomId, dryerId, 'dryers');
};

export const removeCompressor = (rooms: Room[], roomId: number, compressorIndex: number): Room[] => {
  const roomIndex = rooms.findIndex(room => room.id === roomId);
  if (roomIndex === -1) return rooms;

  if (compressorIndex === -1) return rooms;

  const serialNumber = rooms[roomIndex].compressorData[compressorIndex].serialNumber;

  const updatedRooms = [...rooms];

  updatedRooms[roomIndex].compressorData = updatedRooms[roomIndex].compressorData.filter(
    (comp, index) => index !== compressorIndex,
  );

  if (serialNumber) {
    updatedRooms[roomIndex].compressorsInRoom = updatedRooms[roomIndex].compressorsInRoom.filter(
      sn => sn !== serialNumber,
    );
  }

  return updatedRooms;
};

export const updateCompressorSpecDetails = (
  rooms: Room[],
  roomId: number,
  compressorId: string,
  field: string,
  value: number | null,
): Room[] => {
  const roomIndex = rooms.findIndex(room => room.id === roomId);
  if (roomIndex === -1) return rooms;

  const compressorIndex = rooms[roomIndex].compressorData.findIndex(comp => comp.id === compressorId);
  if (compressorIndex === -1) return rooms;

  const fieldMapping = {
    flow: 'customSpecFlow',
    power: 'customSpecPower',
    pressure: 'customSpecPressure',
  };

  const backendField = fieldMapping[field] || field;

  const updatedRooms = [...rooms];
  updatedRooms[roomIndex].compressorData[compressorIndex] = {
    ...updatedRooms[roomIndex].compressorData[compressorIndex],
    [backendField]: value,
  };

  return updatedRooms;
};

export const addRoom = (rooms: Room[]): Room[] => {
  const newRoomId = Date.now();
  const newRoom = {
    id: newRoomId,
    name: `Room ${rooms.length + 1}`,
    compressorData: [],
    compressorsInRoom: [],
    tanks: [],
    dryers: [],
    newDryer: { dryerType: DryerType.REFRIGERATED, id: `room-${newRoomId}-dryer-new` },
  };

  return [...rooms, newRoom];
};

export const removeRoom = (rooms: Room[], roomId: number): Room[] => {
  if (rooms.length <= 1) return rooms;

  let newRooms = rooms.filter(room => room.id !== roomId);

  newRooms = newRooms.map((room, index) => ({
    ...room,
    id: index + 1,
    name: `Room ${index + 1}`,
    compressorData: Array.isArray(room.compressorData) ? room.compressorData : [],
    compressorsInRoom: Array.isArray(room.compressorsInRoom) ? room.compressorsInRoom : [],
    tanks: Array.isArray(room.tanks) ? room.tanks : [],
  }));

  return newRooms;
};
